<!-- collectionOperationDashboard/component/BrazilMap.vue -->
<template>
  <section class="map-card">
    <div class="map-head">
      <div>
        <h2>{{ $t('collectionOperationDashboard.mapTitle') }}</h2>
        <p>{{ $t('collectionOperationDashboard.mapSubtitle') }}</p>
      </div>
      <div class="map-meta">
        <span class="map-live"><i />{{ $t('collectionOperationDashboard.mapLive') }}</span>
        <span>{{ $t('collectionOperationDashboard.mapClickTip') }}</span>
      </div>
    </div>

    <div class="map-legend">
      <span v-for="(color, index) in platformColors" :key="index" class="legend-chip">
        <i :style="{ background: color }" />{{ platformNames[index] }}
      </span>
    </div>

    <div class="map-layout">
      <div class="map-wrap">
        <div ref="brazilMap" class="brazil-map">
          <svg
            :aria-label="$t('collectionOperationDashboard.mapAriaLabel')"
            class="brazil-svg"
            viewBox="0 0 520 410"
          >
            <g ref="mapViewport">
              <path
                class="country-shadow"
                d="M145 35 L215 20 L290 35 L360 50 L430 82 L470 130 L455 190 L430 240 L410 285 L385 330 L345 388 L300 372 L268 340 L220 326 L188 286 L165 248 L130 220 L104 175 L90 125 L112 82 Z"
              />
              <g ref="statePolygons">
                <path
                  v-for="state in stateList"
                  :key="state.code"
                  :class="[
                    'state-shape',
                    stateOrderClass(state.code),
                    { active: selectedState === state.code }
                  ]"
                  :d="state.path"
                  :data-state="state.code"
                  @click="selectState(state.code)"
                  @mouseenter="showMapTooltip($event, state.code)"
                  @mouseleave="hideMapTooltip"
                  @mousemove="moveMapTooltip($event)"
                />
              </g>

              <g ref="stateLabels">
                <text
                  v-for="state in stateList"
                  :key="`label-${state.code}`"
                  :x="state.labelX"
                  :y="state.labelY"
                  class="state-label"
                >
                  {{ state.code }}
                </text>
                <text
                  v-for="state in stateList"
                  :key="`value-${state.code}`"
                  :x="state.labelX"
                  :y="state.labelY + 13"
                  class="state-value"
                >
                  {{ formatNumber(stateValue(state.code)) }}
                </text>
              </g>
            </g>
          </svg>

          <!-- Tooltip -->
          <div
            v-if="tooltip.visible"
            :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
            class="map-tooltip"
          >
            <div class="tooltip-title">
              {{ tooltip.state }} · {{ formatNumber(tooltip.total)
              }}{{ $t('collectionOperationDashboard.piece') }}
            </div>
            <div v-for="(item, i) in tooltip.details" :key="i" class="tooltip-row">
              <i :style="{ background: item.color }" />
              <span>{{ item.name }}</span>
              <b>{{ formatNumber(item.value) }}{{ $t('collectionOperationDashboard.piece') }}</b>
            </div>
            <div v-if="!tooltip.details.length" class="tooltip-row">
              <span>{{ $t('collectionOperationDashboard.mapNoData') }}</span>
            </div>
          </div>

          <div class="map-toolbar">
            <button class="map-tool-btn" @click="zoomMap(1.15)">＋</button>
            <button class="map-tool-btn" @click="zoomMap(0.87)">−</button>
            <button :title="$t('GlobalSetObj.reset')" class="map-tool-btn" @click="resetMapZoom()">
              ↺
            </button>
          </div>
          <div class="map-note">{{ $t('collectionOperationDashboard.mapNote') }}</div>
        </div>
      </div>

      <aside class="map-summary">
        <div class="map-summary-title">
          <b>{{ mapScope }}</b>
          <span>{{ mapMetric }}</span>
        </div>
        <div class="map-total">
          {{ formatNumber(mapTotal) }}<small>{{ $t('collectionOperationDashboard.piece') }}</small>
        </div>
        <div class="map-stats">
          <div class="map-stat">
            <span>{{ $t('collectionOperationDashboard.coverage') }}</span>
            <b>{{ mapCoverage }}</b>
          </div>
          <div class="map-stat">
            <span>{{ $t('collectionOperationDashboard.topClient') }}</span>
            <b>{{ mapTopClient }}</b>
          </div>
        </div>
        <!-- 发件州缺失的量能计入总量但落不到地图，单独说明，避免被当成丢数 -->
        <div v-if="mapUnknownTotal > 0" class="map-unknown">
          {{ $t('collectionOperationDashboard.unknownStateHint', { count: formatNumber(mapUnknownTotal) }) }}
        </div>
        <div class="rank-title">
          <span>{{ $t('collectionOperationDashboard.ufRanking') }}</span>
          <span>{{ $t('collectionOperationDashboard.collectVolume') }}</span>
        </div>
        <div class="map-ranking">
          <div v-for="item in mapRanking" :key="item.name" class="rank-row">
            <span class="rank-uf">{{ item.name }}</span>
            <span class="rank-track">
              <i :style="{ width: item.percent + '%' }" />
            </span>
            <span class="rank-value">{{ formatNumber(item.value) }}</span>
          </div>
        </div>
        <div class="map-foot">{{ $t('collectionOperationDashboard.mapFoot') }}</div>
      </aside>
    </div>
  </section>
</template>

<script>
import {
  normalizePlatform,
  platformColorOf,
  platformColors,
  platformNames
} from '../utils/brazilMap';
import { getFmCollectUfCustomerChart } from '../api';

export default {
  name: 'BrazilMap',
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      platformNames,
      platformColors,
      // ufCustomerChart 原始响应（州×客户揽收矩阵）
      mapCustomerData: [],
      // 归一化后的州×客户矩阵：{ SP: { SHEIN: 120, TEMU: 30, OTHER: 5 } }
      customerMatrix: {},
      // 最近一次查询生效的客户口径（2026-10-23 测试反馈：地图过滤不再跟下拉实时值联动——
      // 数据快照来自点查询，口径若跟下拉走会出现"查B2C后切SHEIN地图全0"的脱节；只在 loadMapCustomerChart 时同步）
      activePlatform: '',
      selectedState: '',
      zoomLevel: 1,
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        state: '',
        total: 0,
        details: []
      },
      // 地图右侧概览指标（由 customerMatrix + filters 计算，mounted 时按当前语言填充）
      mapTotal: 0,
      mapScope: '',
      mapMetric: '',
      mapCoverage: '',
      mapTopClient: '—',
      mapRanking: [],
      // 发件州缺失（邮编解析不到）的揽收量：能计入总量但落不到地图上，单独提示
      mapUnknownTotal: 0,

      // 州数据（含SVG路径和标签位置）
      stateList: [
        {
          code: 'RR',
          path: 'M135 43 L187 30 L214 52 L199 88 L154 91 L126 70 Z',
          labelX: 165,
          labelY: 61,
          pieX: 168,
          pieY: 68,
          pieRadius: 14
        },
        {
          code: 'AM',
          path: 'M101 82 L154 91 L199 88 L230 112 L212 166 L169 177 L119 158 L91 126 Z',
          labelX: 158,
          labelY: 128,
          pieX: 160,
          pieY: 136,
          pieRadius: 18
        },
        {
          code: 'AC',
          path: 'M76 155 L119 158 L126 184 L91 194 L67 180 Z',
          labelX: 94,
          labelY: 173,
          pieX: 96,
          pieY: 180,
          pieRadius: 11
        },
        {
          code: 'RO',
          path: 'M126 165 L169 177 L176 211 L137 220 L111 191 Z',
          labelX: 144,
          labelY: 194,
          pieX: 146,
          pieY: 202,
          pieRadius: 12
        },
        {
          code: 'AP',
          path: 'M214 34 L241 25 L257 52 L245 78 L214 70 Z',
          labelX: 235,
          labelY: 53,
          pieX: 237,
          pieY: 60,
          pieRadius: 10
        },
        {
          code: 'PA',
          path: 'M214 70 L245 78 L292 64 L321 91 L304 132 L265 145 L230 112 L199 88 Z',
          labelX: 263,
          labelY: 104,
          pieX: 265,
          pieY: 112,
          pieRadius: 17
        },
        {
          code: 'TO',
          path: 'M265 145 L304 132 L322 163 L305 204 L276 195 L258 169 Z',
          labelX: 289,
          labelY: 169,
          pieX: 290,
          pieY: 176,
          pieRadius: 12
        },
        {
          code: 'MT',
          path: 'M169 177 L212 166 L258 169 L276 195 L258 235 L205 244 L176 211 Z',
          labelX: 218,
          labelY: 207,
          pieX: 220,
          pieY: 215,
          pieRadius: 16
        },
        {
          code: 'MS',
          path: 'M205 244 L258 235 L276 267 L252 298 L211 286 L189 260 Z',
          labelX: 234,
          labelY: 267,
          pieX: 235,
          pieY: 274,
          pieRadius: 13
        },
        {
          code: 'GO',
          path: 'M276 195 L305 204 L329 225 L310 257 L276 267 L258 235 Z',
          labelX: 294,
          labelY: 234,
          pieX: 296,
          pieY: 242,
          pieRadius: 14
        },
        {
          code: 'DF',
          path: 'M300 220 L309 217 L315 225 L307 232 L298 228 Z',
          labelX: 307,
          labelY: 225,
          pieX: 308,
          pieY: 229,
          pieRadius: 6
        },
        {
          code: 'MA',
          path: 'M304 92 L344 80 L370 100 L360 137 L322 163 L304 132 Z',
          labelX: 334,
          labelY: 113,
          pieX: 336,
          pieY: 120,
          pieRadius: 13
        },
        {
          code: 'PI',
          path: 'M322 163 L360 137 L378 163 L364 198 L329 225 L305 204 Z',
          labelX: 348,
          labelY: 176,
          pieX: 350,
          pieY: 184,
          pieRadius: 12
        },
        {
          code: 'CE',
          path: 'M370 100 L411 101 L425 123 L401 145 L378 163 L360 137 Z',
          labelX: 393,
          labelY: 123,
          pieX: 395,
          pieY: 130,
          pieRadius: 13
        },
        {
          code: 'RN',
          path: 'M411 101 L447 106 L457 120 L425 123 Z',
          labelX: 436,
          labelY: 116,
          pieX: 438,
          pieY: 119,
          pieRadius: 8
        },
        {
          code: 'PB',
          path: 'M401 145 L442 137 L454 150 L414 159 L378 163 Z',
          labelX: 421,
          labelY: 151,
          pieX: 423,
          pieY: 154,
          pieRadius: 9
        },
        {
          code: 'PE',
          path: 'M378 163 L414 159 L456 161 L448 177 L402 181 L364 198 Z',
          labelX: 409,
          labelY: 171,
          pieX: 411,
          pieY: 178,
          pieRadius: 13
        },
        {
          code: 'AL',
          path: 'M402 181 L448 177 L451 190 L414 194 Z',
          labelX: 429,
          labelY: 186,
          pieX: 431,
          pieY: 189,
          pieRadius: 7
        },
        {
          code: 'SE',
          path: 'M414 194 L451 190 L447 203 L421 207 Z',
          labelX: 433,
          labelY: 200,
          pieX: 435,
          pieY: 203,
          pieRadius: 6
        },
        {
          code: 'BA',
          path: 'M364 198 L402 181 L414 194 L421 207 L406 251 L370 267 L329 225 Z',
          labelX: 375,
          labelY: 225,
          pieX: 377,
          pieY: 233,
          pieRadius: 16
        },
        {
          code: 'MG',
          path: 'M310 257 L329 225 L370 267 L398 272 L381 301 L337 309 L296 288 Z',
          labelX: 342,
          labelY: 279,
          pieX: 344,
          pieY: 287,
          pieRadius: 16
        },
        {
          code: 'ES',
          path: 'M398 272 L421 261 L426 286 L405 300 L381 301 Z',
          labelX: 409,
          labelY: 282,
          pieX: 411,
          pieY: 289,
          pieRadius: 10
        },
        {
          code: 'RJ',
          path: 'M381 301 L405 300 L412 314 L382 320 L359 315 Z',
          labelX: 390,
          labelY: 310,
          pieX: 392,
          pieY: 314,
          pieRadius: 9
        },
        {
          code: 'SP',
          path: 'M296 288 L337 309 L359 315 L344 338 L293 337 L252 313 L252 298 L276 267 Z',
          labelX: 305,
          labelY: 317,
          pieX: 307,
          pieY: 325,
          pieRadius: 17
        },
        {
          code: 'PR',
          path: 'M252 313 L293 337 L301 354 L266 364 L229 342 L211 286 L252 298 Z',
          labelX: 263,
          labelY: 337,
          pieX: 265,
          pieY: 344,
          pieRadius: 15
        },
        {
          code: 'SC',
          path: 'M266 364 L301 354 L323 368 L306 383 L271 380 Z',
          labelX: 293,
          labelY: 370,
          pieX: 295,
          pieY: 375,
          pieRadius: 11
        },
        {
          code: 'RS',
          path: 'M271 380 L306 383 L296 405 L251 399 L229 371 L229 342 L266 364 Z',
          labelX: 267,
          labelY: 392,
          pieX: 269,
          pieY: 397,
          pieRadius: 12
        }
      ]
    };
  },
  computed: {
    // 地图口径取最近一次查询生效的客户（activePlatform），不跟下拉实时值联动（2026-10-23 测试反馈）
    metricPlatform() {
      return this.activePlatform;
    },
    // 地图概览统计范围：优先地图点击选中的州，其次顶部 UF 筛选（仅本地统计，不触发接口）
    scopeUf() {
      return this.selectedState || this.filters.uf || '';
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        // 顶部 UF 筛选有值时以顶部为准，清掉地图本地选中的州
        if (this.filters.uf) {
          this.selectedState = '';
        }
        this.renderMapSummary();
      }
    },
    // 切换语言不会刷新页面，概览区文案需按新语言重算
    '$i18n.locale'() {
      this.renderMapSummary();
    }
  },
  mounted() {
    this.renderMapSummary();
  },
  methods: {
    getBody(res) {
      if (res && typeof res === 'object' && Object.prototype.hasOwnProperty.call(res, 'data')) {
        return res.data;
      }
      return res;
    },

    // 供父级“重置”时调用：清空地图点选的州，恢复全国概览
    reset() {
      this.selectedState = '';
      this.activePlatform = '';
      this.hideMapTooltip();
      this.renderMapSummary();
    },

    // 供父级“查询”时统一调用
    fetchData(params) {
      return this.loadMapCustomerChart(params);
    },

    // 按州×客户揽收量（地图州内圆环、第一大客户）
    async loadMapCustomerChart(params) {
      try {
        const res = await getFmCollectUfCustomerChart({
          startDate: params.startDate,
          endDate: params.endDate,
          platform: params.platform,
          serviceType: params.serviceType,
          stateCode: params.stateCode,
          cityName: params.cityName,
          siteIds: params.siteIds
        });
        console.log('[BrazilMap] 按州×客户 ufCustomerChart 响应：', res);
        const body = this.getBody(res);
        const list = Array.isArray(body) ? body : body && Array.isArray(body.list) ? body.list : [];
        this.mapCustomerData = list;
        this.customerMatrix = this.buildCustomerMatrix(list);
        // 口径与数据同源于查询：本次请求带的 platform 即地图生效口径，切下拉不点查询地图不动
        this.activePlatform = params.platform ? normalizePlatform(params.platform) : '';
        this.renderMapSummary();
      } catch (error) {
        console.error('[BrazilMap] 按州×客户加载失败：', error);
      }
    },

    // 兼容多种响应形态，统一归一化为 { UF: { 客户: 揽收量 } }
    // 1) 扁平：[{ stateCode, platform, collectTotal }]
    // 2) 分组：[{ stateCode, list: [{ platform, collectTotal }] }]
    // 3) 字典：[{ stateCode, platforms: { SHEIN: 1, TEMU: 2 } }]
    // 4) 平台为键：[{ stateCode, SHEIN: 1, TEMU: 2 }]
    buildCustomerMatrix(raw) {
      const matrix = {};
      const ignoredKeys = ['stateCode', 'uf', 'state', 'total', 'collectTotal', 'loadedTotal'];
      const add = (stateCode, platform, value) => {
        const uf = String(stateCode || '')
          .trim()
          .toUpperCase();
        const num = Number(value);
        if (!uf || !Number.isFinite(num)) return;
        const name = normalizePlatform(platform);
        if (!matrix[uf]) matrix[uf] = {};
        matrix[uf][name] = (matrix[uf][name] || 0) + num;
      };
      const readPlatform = (item) =>
        item.platform || item.platformName || item.name || item.customer || item.client || '';
      const readValue = (item) => {
        const values = [item.collectTotal, item.value, item.total, item.orders, item.orderTotal];
        const hit = values.find((v) => v !== undefined && v !== null && v !== '');
        return hit === undefined ? 0 : hit;
      }

      ;(Array.isArray(raw) ? raw : []).forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const stateCode = item.stateCode || item.uf || item.state || '';
        const children =
          item.list ||
          item.platforms ||
          item.items ||
          item.children ||
          item.customerList ||
          item.records;
        if (Array.isArray(children)) {
          children.forEach((child) => {
            if (child && typeof child === 'object') {
              add(stateCode, readPlatform(child), readValue(child));
            }
          });
          return;
        }
        if (children && typeof children === 'object') {
          Object.keys(children).forEach((key) => add(stateCode, key, children[key]));
          return;
        }
        if (readPlatform(item)) {
          add(stateCode, readPlatform(item), readValue(item));
          return;
        }
        Object.keys(item).forEach((key) => {
          if (ignoredKeys.includes(key)) return;
          if (typeof item[key] === 'number') add(stateCode, key, item[key]);
        });
      });

      return matrix;
    },

    // 某州某客户揽收量
    platformValue(code, platform) {
      const row = this.customerMatrix[code];
      if (!row || !platform) return 0;
      return Number(row[normalizePlatform(platform)]) || 0;
    },

    // 某州全部客户揽收量合计
    stateTotal(code) {
      const row = this.customerMatrix[code] || {};
      return Object.keys(row).reduce((sum, key) => sum + (Number(row[key]) || 0), 0);
    },

    // 当前口径下某州数值：选了客户取该客户，未选客户取全客户合计
    stateValue(code) {
      return this.metricPlatform
        ? this.platformValue(code, this.metricPlatform)
        : this.stateTotal(code);
    },

    // 指定范围（州或全国）内揽收量最高的客户
    topPlatform(scope) {
      const states = scope ? [scope] : Object.keys(this.customerMatrix);
      const totals = {};
      states.forEach((code) => {
        const row = this.customerMatrix[code] || {};
        Object.keys(row).forEach((name) => {
          totals[name] = (totals[name] || 0) + (Number(row[name]) || 0);
        });
      });
      return Object.keys(totals).sort((a, b) => totals[b] - totals[a])[0] || '';
    },

    // 当前口径下某州的客户明细（含颜色），用于 tooltip
    platformEntries(code) {
      if (this.metricPlatform) {
        const value = this.platformValue(code, this.metricPlatform);
        return value > 0
          ? [{ name: this.metricPlatform, value, color: platformColorOf(this.metricPlatform) }]
          : [];
      }
      const row = this.customerMatrix[code] || {};
      return Object.keys(row)
        .map((name) => ({ name, value: Number(row[name]) || 0, color: platformColorOf(name) }))
        .filter((entry) => entry.value > 0)
        .sort((a, b) => b.value - a.value);
    },

    formatNumber(num) {
      return new Intl.NumberFormat('zh-CN').format(Math.round(Number(num) || 0));
    },

    // 州面颜色分档：有任何揽收量的州至少为 state-low，避免与无数据州（state-min）混淆
    stateOrderClass(code) {
      const value = this.stateValue(code);
      if (!value) return 'state-min';
      const values = this.stateList.map((state) => this.stateValue(state.code)).filter((v) => v > 0);
      const max = values.length ? Math.max(...values) : 0;
      if (!max) return 'state-low';
      if (value >= max * 0.5) return 'state-high';
      if (value >= max * 0.15) return 'state-mid';
      return 'state-low';
    },

    // 点击州仅在本地切换右侧概览，不触发其它看板接口
    selectState(code) {
      this.selectedState = this.selectedState === code ? '' : code;
      this.renderMapSummary();
    },

    showMapTooltip(e, code) {
      this.tooltip = {
        visible: true,
        state: code,
        total: this.stateValue(code),
        details: this.platformEntries(code)
      };
      this.moveMapTooltip(e);
    },

    hideMapTooltip() {
      this.tooltip.visible = false;
    },

    moveMapTooltip(e) {
      const rect = this.$refs.brazilMap.getBoundingClientRect();
      this.tooltip.x = e.clientX - rect.left + 15;
      this.tooltip.y = e.clientY - rect.top + 15;
    },

    zoomMap(factor) {
      this.zoomLevel = Math.max(0.5, Math.min(2.5, this.zoomLevel * factor));
      const viewport = this.$refs.mapViewport;
      if (viewport) {
        viewport.style.transform = `scale(${this.zoomLevel})`;
        viewport.style.transformOrigin = 'center';
      }
    },

    resetMapZoom() {
      this.zoomLevel = 1;
      const viewport = this.$refs.mapViewport;
      if (viewport) {
        viewport.style.transform = 'scale(1)';
      }
    },

    renderMapSummary() {
      const scopeUf = this.scopeUf;
      // UNKNOWN 是「发件州缺失」的兜底桶：不参与 UF 覆盖数与排名，但必须计入揽收总量，
      // 否则会出现「KPI 3 件 / 地图 0 件」的自相矛盾（2026-10-26 测试反馈）
      const allStates = scopeUf ? [scopeUf] : Object.keys(this.customerMatrix);
      const activeStates = allStates.filter((code) => code !== 'UNKNOWN');
      const unknownTotal = scopeUf ? 0 : this.stateValue('UNKNOWN');

      const ranking = activeStates
        .map((code) => ({ name: code, value: this.stateValue(code) }))
        .sort((a, b) => b.value - a.value);

      this.mapScope = scopeUf
        ? this.$t('collectionOperationDashboard.stateOverview', { uf: scopeUf })
        : this.$t('collectionOperationDashboard.nationalOverview');
      this.mapMetric = this.metricPlatform
        ? this.$t('collectionOperationDashboard.platformCollect', { platform: this.metricPlatform })
        : this.$t('collectionOperationDashboard.collectTotal');
      this.mapTotal = ranking.reduce((sum, item) => sum + item.value, 0) + unknownTotal;
      // 覆盖范围如实取有数据且有州的 UF 数；0 件时就是 0，不再用 27 兜底
      // 选中单州时同理：该州没数据就显示 0 UF，而不是恒等于 1
      this.mapCoverage = this.$t('collectionOperationDashboard.ufCount', {
        count: scopeUf ? (ranking.some((item) => item.value > 0) ? 1 : 0) : activeStates.length
      });
      this.mapUnknownTotal = unknownTotal;
      this.mapTopClient = this.metricPlatform || this.topPlatform(scopeUf) || '—';

      const top = ranking[0] ? ranking[0].value : 0;
      this.mapRanking = ranking.slice(0, 6).map((item) => ({
        name: item.name,
        value: item.value,
        percent: top ? Math.max(5, (item.value / top) * 100) : 5
      }));
    }
  }
};
</script>

<style lang="scss" scoped>
.map-card {
  margin: 0 0 22px;
  border: 1px solid #e1e9e5;
  border-radius: 19px;
  background: linear-gradient(145deg, #fff 0%, #fbfefc 72%, #f0faf5 100%);
  box-shadow: 0 1px 2px rgba(16, 36, 27, 0.03), 0 12px 32px rgba(16, 36, 27, 0.055);
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto;
    height: 3px;
    background: linear-gradient(90deg, #05603a, #12b76a 38%, #8fe0b9 70%, transparent);
  }

  .map-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 10px;
    gap: 18px;

    h2 {
      font-size: 17px;
      margin: 0;
      color: #16362a;
    }

    p {
      font-size: 11px;
      color: #76877f;
      margin: 4px 0 0;
    }

    .map-meta {
      display: flex;
      align-items: center;
      gap: 12px;
      color: #75877e;
      font-size: 11px;
      white-space: nowrap;

      .map-live {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 6px 9px;
        border-radius: 999px;
        background: #eaf8f1;
        color: #05603a;
        font-weight: 800;

        i {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #12b76a;
          box-shadow: 0 0 0 4px rgba(18, 183, 106, 0.12);
        }
      }
    }
  }

  .map-legend {
    display: flex;
    gap: 7px;
    flex-wrap: wrap;
    padding: 0 20px 5px;

    .legend-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 8px;
      border-radius: 999px;
      background: #fff;
      border: 1px solid #edf2ef;
      color: #53685e;
      font-size: 11px;
      font-weight: 700;

      i {
        width: 8px;
        height: 8px;
        border-radius: 50%;
      }
    }
  }

  .map-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 292px;
    gap: 4px;
    align-items: stretch;
    padding: 0 14px 16px 8px;

    .map-wrap {
      position: relative;
      min-width: 0;
      border-radius: 15px;
      background: radial-gradient(
        circle at 50% 45%,
        rgba(234, 248, 241, 0.86),
        rgba(255, 255, 255, 0.2) 54%,
        transparent 72%
      );
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 560px;
    }

    .brazil-map {
      position: relative;
      width: 100%;
      height: 560px;
      display: flex;
      align-items: center;
      justify-content: center;

      .brazil-svg {
        width: 100%;
        height: 100%;
        max-width: 650px;
        cursor: grab;

        &:active {
          cursor: grabbing;
        }

        .country-shadow {
          fill: none;
          stroke: #c8e6d4;
          stroke-width: 2;
          stroke-dasharray: 4 4;
        }

        .state-shape {
          fill: #e8f5ee;
          stroke: #fff;
          stroke-width: 1.5;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            fill: #b9e6cf;
            stroke: #079455;
            stroke-width: 2;
          }

          &.active {
            fill: #48c98a;
            stroke: #05603a;
            stroke-width: 2.5;
          }

          &.state-high {
            fill: #079455;
          }

          &.state-mid {
            fill: #4cd08d;
          }

          &.state-low {
            fill: #a8e5c6;
          }

          &.state-min {
            fill: #e8f5ee;
          }
        }

        .state-label {
          font-size: 9px;
          font-weight: 800;
          fill: #2f453b;
          text-anchor: middle;
          pointer-events: none;
        }

        .state-value {
          font-size: 8px;
          font-weight: 600;
          fill: #76877f;
          text-anchor: middle;
          pointer-events: none;
        }
      }

      .map-tooltip {
        position: absolute;
        z-index: 100;
        background: rgba(16, 35, 27, 0.96);
        border-radius: 10px;
        padding: 10px 12px;
        color: #fff;
        font-size: 11px;
        box-shadow: 0 12px 30px rgba(16, 35, 27, 0.22);
        pointer-events: none;
        max-width: 180px;

        .tooltip-title {
          font-weight: 800;
          margin-bottom: 6px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.15);
          padding-bottom: 4px;
        }

        .tooltip-row {
          display: flex;
          align-items: center;
          gap: 6px;
          margin: 3px 0;

          i {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            display: inline-block;
          }

          span {
            flex: 1;
          }

          b {
            font-weight: 600;
          }
        }
      }

      .map-toolbar {
        position: absolute;
        right: 12px;
        bottom: 12px;
        display: flex;
        flex-direction: column;
        gap: 4px;

        .map-tool-btn {
          width: 28px;
          height: 28px;
          border: 1px solid #d9e8e0;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.9);
          color: #65786e;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;

          &:hover {
            background: #fff;
            border-color: #079455;
            color: #079455;
          }
        }
      }

      .map-note {
        position: absolute;
        left: 12px;
        bottom: 12px;
        font-size: 10px;
        color: #7c8e85;
        background: rgba(255, 255, 255, 0.8);
        padding: 4px 8px;
        border-radius: 6px;
      }
    }

    .map-summary {
      align-self: center;
      border: 1px solid #e1e9e5;
      border-radius: 15px;
      background: rgba(255, 255, 255, 0.9);
      padding: 16px;
      box-shadow: 0 14px 32px rgba(16, 36, 27, 0.055);

      .map-summary-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: #667970;
        font-size: 11px;
        margin-bottom: 6px;

        b {
          color: #05603a;
          font-size: 12px;
        }
      }

      .map-total {
        font-size: 29px;
        font-weight: 900;
        letter-spacing: -0.7px;
        color: #14362a;

        small {
          font-size: 11px;
          font-weight: 700;
          color: #76877f;
          margin-left: 4px;
        }
      }

      .map-stats {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 7px;
        margin: 12px 0;

        .map-stat {
          padding: 9px;
          border-radius: 10px;
          background: #f5faf7;
          border: 1px solid #e6f0eb;

          span {
            display: block;
            color: #7a8c83;
            font-size: 10px;
          }

          b {
            display: block;
            color: #29473a;
            margin-top: 2px;
          }
        }
      }

      .map-unknown {
        margin: -6px 0 10px;
        padding: 6px 9px;
        border-radius: 8px;
        background: #fff8ec;
        border: 1px solid #ffe6bd;
        color: #a8681a;
        font-size: 10px;
        line-height: 1.4;
      }

      .rank-title {
        display: flex;
        justify-content: space-between;
        margin: 15px 0 8px;
        color: #60746a;
        font-size: 11px;
        font-weight: 800;
      }

      .rank-row {
        display: grid;
        grid-template-columns: 28px 1fr auto;
        gap: 7px;
        align-items: center;
        margin: 8px 0;

        .rank-uf {
          display: grid;
          place-items: center;
          height: 24px;
          border-radius: 7px;
          background: #eaf8f1;
          color: #05603a;
          font-size: 10px;
          font-weight: 900;
        }

        .rank-track {
          height: 6px;
          background: #edf3f0;
          border-radius: 999px;
          overflow: hidden;

          i {
            display: block;
            height: 100%;
            border-radius: inherit;
            background: linear-gradient(90deg, #079455, #48ca8b);
          }
        }

        .rank-value {
          font-size: 10px;
          color: #52675d;
          font-variant-numeric: tabular-nums;
        }
      }

      .map-foot {
        margin-top: 13px;
        padding-top: 11px;
        border-top: 1px dashed #dce8e2;
        color: #7c8e85;
        font-size: 10px;
        line-height: 1.6;
      }
    }
  }
}

@media (max-width: 1050px) {
  .map-layout {
    grid-template-columns: 1fr;

    .map-summary {
      margin: 0 8px;
    }
  }
}

@media (max-width: 700px) {
  .map-head {
    display: block;

    .map-meta {
      margin-top: 10px;
      justify-content: flex-start;
    }
  }

  .map-layout {
    padding: 0 7px 12px;
  }

  .brazil-map {
    height: 430px !important;
  }
}
</style>
